import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { spacesListQueryOptions } from "@/modules/spaces/spaces-queries";

interface Props {
  /** Title shown at top of the dialog. */
  title: string;
  /** Friendly name of the source resource ("Yandex Mail"); used in the
   *  hint line for context. */
  sourceLabel: string;
  /** Space id where the source currently lives. Pre-excluded from the
   *  destination list so the user can't copy onto themselves. */
  sourceSpaceId: string | null;
  /** Should perform the copy. The dialog closes via `onClose` on success
   *  and renders the error inline on failure. */
  onCopy: (toSpaceId: string | null) => Promise<unknown>;
  onClose: () => void;
  /** Cache keys to invalidate after a successful copy — caller knows
   *  which catalogue (connectors / skills / agents) to refresh. */
  invalidateKeys?: string[][];
}

/**
 * Modal: "Скопировать в …".
 *
 * Lists every space the caller is a member of plus a synthetic
 * «Личное» entry for the personal scope (`null`). The current scope of
 * the source is pre-disabled so the user can't pick a no-op target.
 *
 * The dialog is shape-agnostic — works for connectors, skills, agents
 * by accepting `onCopy` from the caller.
 */
export function CopyToSpaceDialog({
  title,
  sourceLabel,
  sourceSpaceId,
  onCopy,
  onClose,
  invalidateKeys,
}: Props) {
  const qc = useQueryClient();
  const { data: spaces = [], isLoading } = useQuery(spacesListQueryOptions());
  const [target, setTarget] = useState<string | null>(
    // Default to a sensible non-source pick: personal if source was in a
    // space, first team space otherwise. Falls through to null = personal
    // when the user has no team spaces.
    sourceSpaceId ? null : (spaces.find((s) => s.id !== sourceSpaceId)?.id ?? null),
  );

  const mutation = useMutation({
    mutationFn: () => onCopy(target),
    onSuccess: () => {
      invalidateKeys?.forEach((k) => qc.invalidateQueries({ queryKey: k }));
      onClose();
    },
  });

  return (
    <div className="copy-dialog-backdrop" onClick={onClose}>
      <div className="copy-dialog" onClick={(e) => e.stopPropagation()}>
        <header className="copy-dialog-head">
          <h2 className="copy-dialog-title">{title}</h2>
          <button className="copy-dialog-close" onClick={onClose} aria-label="Закрыть">×</button>
        </header>

        <p className="copy-dialog-hint">
          Куда скопировать «{sourceLabel}»?
        </p>

        {isLoading ? (
          <div className="copy-dialog-loading">Загрузка…</div>
        ) : (
          <ul className="copy-dialog-list">
            <ScopeOption
              label="Личное"
              hint="Доступен только вам"
              checked={target === null}
              disabled={sourceSpaceId === null}
              onPick={() => setTarget(null)}
            />
            {spaces.map((s) => (
              <ScopeOption
                key={s.id}
                label={s.name}
                hint={s.description || undefined}
                checked={target === s.id}
                disabled={sourceSpaceId === s.id}
                onPick={() => setTarget(s.id)}
              />
            ))}
          </ul>
        )}

        {mutation.error && (
          <div className="copy-dialog-error">
            {(mutation.error as Error).message}
          </div>
        )}

        <footer className="copy-dialog-foot">
          <button
            type="button"
            className="copy-dialog-btn copy-dialog-btn--ghost"
            onClick={onClose}
          >
            Отмена
          </button>
          <button
            type="button"
            className="copy-dialog-btn copy-dialog-btn--primary"
            disabled={mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending ? "Копируем…" : "Скопировать"}
          </button>
        </footer>
      </div>
    </div>
  );
}

function ScopeOption({
  label, hint, checked, disabled, onPick,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  disabled?: boolean;
  onPick: () => void;
}) {
  return (
    <li>
      <button
        type="button"
        className={`copy-dialog-item${checked ? " copy-dialog-item--checked" : ""}`}
        disabled={disabled}
        onClick={onPick}
        title={disabled ? "Уже здесь" : undefined}
      >
        <span className={`copy-dialog-radio${checked ? " copy-dialog-radio--on" : ""}`} />
        <span className="copy-dialog-item-text">
          <span className="copy-dialog-item-label">{label}</span>
          {hint && <span className="copy-dialog-item-hint">{hint}</span>}
        </span>
        {disabled && <span className="copy-dialog-item-stub">текущее</span>}
      </button>
    </li>
  );
}
